import React from "react";
import styles from "../../DynamicForm.module.scss";
import { renderStandardInput } from "./StandardInputRenderer";
import { createSerialKey, cleanValue } from "../../utils/formHelpers";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

/**
 * Listen-Darstellung für Formularelemente
 */
const ListRepresentation = ({ items, step, stepIndex, itemIndex }) => {
    // Teile die Items in Listen-Items und Standard-Items auf
    const listItems = items.filter(item => item.type === 9 || item.type === 10);
    const standardItems = items.filter(item => item.type !== 9 && item.type !== 10);
    
    // Gruppiere die Radio-Buttons nach Name
    const radioGroups = {};
    listItems.forEach(item => {
        if (item.type === 10 && item.required) {
            const name = `multi-${stepIndex}-${itemIndex}`;
            if (!radioGroups[name]) {
                radioGroups[name] = true;
            }
        }
    });

    const handleListItemClick = (e, input) => {
        // Vermeide doppelte Ausführung, wenn direkt auf Input geklickt wurde
        if (e.target === input) return;
        
        if (!input || input.disabled) return;
        
        const isRadio = input.type === 'radio';
        const isCheckbox = input.type === 'checkbox';
        
        // Bei Radio sicherstellen, dass nur eines ausgewählt werden kann
        if (isRadio) {
            if (input.checked) {
                // Wenn bereits ausgewählt, nichts tun
                return;
            }
            
            // Setze Radio auf checked und aktualisiere die UI
            input.checked = true;
            
            // Entferne aktiven Status bei allen anderen Radio-Buttons in der Gruppe
            const allRadios = document.querySelectorAll(`input[type="radio"][name="${input.name}"]`);
            allRadios.forEach(radio => {
                const listItem = radio.closest('.list-group-item');
                if (listItem) {
                    if (radio === input) {
                        listItem.classList.add('active');
                    } else {
                        listItem.classList.remove('active');
                        radio.checked = false;
                    }
                }
            });
        } else if (isCheckbox) {
            // Toggle Checkbox
            input.checked = !input.checked;
            
            // Toggle aktive Klasse
            const listItem = input.closest('.list-group-item');
            if (listItem) {
                if (input.checked) {
                    listItem.classList.add('active');
                } else {
                    listItem.classList.remove('active');
                }
            }
        }

        // Manuelles Change-Event auslösen
        const changeEvent = new Event('change', { bubbles: true });
        input.dispatchEvent(changeEvent);
    };
    
    return (
        <>
            <div className={styles.listContainer}>
                <ul className="list-group list-group-flush border-0" data-list-container>
                    {listItems.map((item, idx) => {
                        // Für Radio-Pflichtfelder
                        const isFirstRequiredRadio = item.type === 10 && item.required && 
                            !listItems.slice(0, idx).some(prevItem => 
                                prevItem.type === 10 && prevItem.required
                            );
                        
                        // Bereinige das Label von unerwünschten Zeichen
                        const cleanedLabel = cleanValue(item.label);
                        
                        return (
                        <li 
                            key={idx} 
                            className={`list-group-item d-flex align-items-center ${item.defaultChecked ? 'active' : ''} cursor-pointer p-3`}
                            onClick={(e) => {
                                const input = e.currentTarget.querySelector('input');
                                handleListItemClick(e, input);
                            }}
                        >
                            {/* Check-Icon Container - korrigiert */}
                            <div className={styles.checkIconContainer}>
                                <FontAwesomeIcon 
                                    icon={faCheck} 
                                    className={styles.checkIcon}
                                    aria-hidden="true"
                                />
                            </div>
                            
                            {/* Rest der Liste */}
                            {item.type === 9 || item.type === 10 ? (
                                <div className="form-check w-100 text-center">
                                    <input
                                        className="form-check-input me-2"
                                        type={item.type === 9 ? "checkbox" : "radio"}
                                        id={`${step.title}.${item.label}`}
                                        name={item.type === 10 ? `radio_group_${stepIndex}` : `${step.title}.${item.label}`}
                                        required={isFirstRequiredRadio || !!item.required}
                                        data-serial-key={createSerialKey(step, item.label, idx)}
                                        value={item.value || item.label}
                                        onChange={(e) => {
                                            // Aktiv-Klasse für das List-Item
                                            const listItem = e.target.closest('.list-group-item');
                                            if (e.target.type === 'checkbox') {
                                                if (e.target.checked) {
                                                    listItem.classList.add('active');
                                                } else {
                                                    listItem.classList.remove('active');
                                                }
                                            } else if (e.target.type === 'radio') {
                                                // Für Radio-Buttons alle anderen deselektieren
                                                const allItems = e.target.closest('.list-group')
                                                    .querySelectorAll('.list-group-item');
                                                allItems.forEach(item => {
                                                    const radio = item.querySelector('input[type="radio"]');
                                                    if (radio && radio.name === e.target.name) {
                                                        if (radio === e.target) {
                                                            item.classList.add('active');
                                                        } else {
                                                            item.classList.remove('active');
                                                        }
                                                    }
                                                });
                                            }
                                        }}
                                        defaultChecked={item.defaultChecked}
                                    />
                                    <label 
                                        className={`form-check-label d-flex flex-column align-items-center ${styles.listItemText}`} 
                                        htmlFor={`${step.title}.${item.label}`}
                                        lang="de" // Wichtig für deutsche Silbentrennung
                                    >
                                        <strong className="mb-1">{cleanedLabel}</strong>
                                        {isFirstRequiredRadio && <span className="ms-1 text-danger">*</span>}
                                        {item.description && (
                                            <div className="form-text small text-muted" lang="de">
                                                {item.description}
                                            </div>
                                        )}
                                    </label>
                                </div>
                            ) : (
                                <div className={`w-100 text-center ${styles.listItemText}`} lang="de">
                                    <strong>{cleanedLabel}</strong>
                                    {item.description && (
                                        <div className="form-text small text-muted" lang="de">
                                            {item.description}
                                        </div>
                                    )}
                                </div>
                            )}
                        </li>
                    )})}
                </ul>
            </div>
            
            {/* Standard-Items für alle anderen Feld-Typen - mit spezieller data-text-input Markierung für SelectionCounter */}
            {standardItems.length > 0 && (
                <div className="mt-4">
                    {standardItems.map((item, idx) => {
                        // Add data-selection-item for SelectionCounter
                        const itemWithSelection = {
                            ...item,
                            "data-selection-item": "true"
                        };
                        
                        return renderStandardInput(itemWithSelection, step, idx, {
                            key: `standard-${idx}`,
                            wrapperClassName: "mb-3"
                        });
                    })}
                </div>
            )}
        </>
    );
};

export default ListRepresentation;
